<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  labAccreditationsApi,
  labsApi,
  type Lab,
  type LabAccreditation,
  type LabAccreditationGridRow,
} from '@/services/api/emsl';
import { useNotifier } from '@/composables/notifier';
import { useGridSelection } from '@/composables/useGridSelection';
import { useGridFillHandle } from '@/composables/useGridFillHandle';
import { useGridClipboard } from '@/composables/useGridClipboard';
import { useGridKeyboard } from '@/composables/useGridKeyboard';
import { formatDateInput, isExpired as isDateExpired, normalizeDate } from '@/utils/spreadsheetDates';
import { cellKey, inRect, parseCellKey } from '@/utils/gridRange';

const notify = useNotifier();

// ── Column definitions ──────────────────────────────────────────────────────
const COLS = [
  'ELLAP', 'EMLAP', 'IHLAP', 'NVLAP',
  'AK', 'AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC',
  'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
  'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
  'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
  'WV', 'WI', 'WY',
];

// ── State ───────────────────────────────────────────────────────────────────
const labs       = ref<Lab[]>([]);
const rows       = ref<LabAccreditationGridRow[]>([]);
const allExpired = ref<LabAccreditation[]>([]);

const selectedLabId = ref<number | ''>('');
const loading       = ref(false);

const labPickerOpen = ref(false);
const labPickerRef  = ref<HTMLElement | null>(null);

const gridRef = ref<HTMLElement | null>(null);

// ── Local directive: auto-focus the input the moment it mounts ───────────────
const vFocus = { mounted: (el: HTMLElement) => el.focus() };

// ── Edit state ───────────────────────────────────────────────────────────────
const editing  = ref<{ ri: number; ci: number } | null>(null);
// When editing is opened by typing a digit (Excel "type to replace"), the first
// keystroke seeds the input instead of the cell's stored value.
const editSeed = ref<string | null>(null);

// ── Grid engine ───────────────────────────────────────────────────────────────
// The Excel-like interaction model (range/multi selection, drag-fill, copy/paste,
// keyboard nav) lives in reusable, grid-agnostic composables; this view supplies
// the column keys, row data, and the date-specific persistence below.
const selection = useGridSelection({
  rowCount: () => rows.value.length,
  colCount: () => COLS.length,
});
const { anchor: selAnchor, focus: selFocus, multiCells, isSelected, isFillHandle } = selection;

const readValue = (ri: number, ci: number): string | null =>
  (rows.value[ri]?.[COLS[ci]!] as string | null) ?? null;
const readText = (ri: number, ci: number): string => readValue(ri, ci) ?? '';
// Guard shared by copy/paste and keyboard: defer to native behaviour while the
// user is typing in the cell input (or any other form field).
const inGridEdit = () => editing.value !== null || inFormField();

const fill = useGridFillHandle({
  selection,
  colName: (ci) => COLS[ci]!,
  readCell: readValue,
  apply: applyEdits,
});

const clipboard = useGridClipboard({
  selection,
  colName: (ci) => COLS[ci]!,
  colCount: () => COLS.length,
  rowCount: () => rows.value.length,
  cellText: readText,
  normalize: normalizeDate,
  apply: applyEdits,
  isBusy: inGridEdit,
  onEmpty: () => notify.error('Nothing pasted — invalid date format'),
  onSkip: (n) => notify.error(`${n} cell(s) skipped — invalid date format`),
});

const keyboard = useGridKeyboard({
  selection,
  isBusy: inGridEdit,
  onEdit: (ri, ci, seed = null) => startEdit(ri, ci, seed),
  onClear: clearSelection,
  onEscape: clearMatchPrompt,
});

// ── "Also update matching cells" prompt (Option 3) ───────────────────────────
// Shown after a single-cell edit when other cells in the SAME row still hold the
// old date. Anchored (fixed-position) just below the edited cell.
const matchPrompt = ref<{
  ri: number;
  cols: string[];
  prev: string;
  next: string;
  top: number;
  left: number;
} | null>(null);

function clearMatchPrompt() {
  matchPrompt.value = null;
}

// ── Derived ─────────────────────────────────────────────────────────────────
const today = new Date().toISOString().slice(0, 10);

function isExpired(v: unknown): boolean {
  return isDateExpired(v, today);
}

const expiredCount = computed(() =>
  rows.value.reduce((n, r) => n + COLS.filter((c) => isExpired(r[c])).length, 0),
);

// How many columns in a single row are still expired — drives the per-row badge
// so it's clear *why* a row stays highlighted (often an expired column is scrolled
// off-screen in the 55-wide grid). Reactive: recomputes as cells are edited.
function rowExpiredCount(row: LabAccreditationGridRow): number {
  return COLS.reduce((n, c) => n + (isExpired(row[c]) ? 1 : 0), 0);
}

function cellValue(row: LabAccreditationGridRow, col: string): string {
  const v = row[col];
  return typeof v === 'string' ? v : '';
}

function isEditing(ri: number, ci: number): boolean {
  return editing.value?.ri === ri && editing.value?.ci === ci;
}

// ── Per-lab expiration counts (drives the lab picker badges) ──────────────────
const expiredByLab = computed(() => {
  const map = new Map<number, number>();
  for (const item of allExpired.value) {
    map.set(item.lab_id, (map.get(item.lab_id) ?? 0) + 1);
  }
  return map;
});

const labsWithCounts = computed(() =>
  [...labs.value]
    .map(lab => ({ ...lab, expiredCount: expiredByLab.value.get(lab.id) ?? 0 }))
    .sort((a, b) => b.expiredCount - a.expiredCount || a.name.localeCompare(b.name)),
);

const selectedLab = computed(() => labs.value.find(l => l.id === selectedLabId.value) ?? null);

const selectedLabExpiredCount = computed(() =>
  selectedLabId.value ? (expiredByLab.value.get(selectedLabId.value as number) ?? 0) : 0,
);

// ── Click-outside to close picker ────────────────────────────────────────────
function handleClickOutside(e: MouseEvent) {
  if (labPickerRef.value && !labPickerRef.value.contains(e.target as Node)) {
    labPickerOpen.value = false;
  }
}

// ── Load ─────────────────────────────────────────────────────────────────────
onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('mouseup', onDocMouseUp);
  document.addEventListener('keydown', keyboard.onKeyDown);
  document.addEventListener('copy', clipboard.onCopy);
  document.addEventListener('paste', clipboard.onPaste);
  const [labsData, expiredData] = await Promise.all([
    labsApi.index() as Promise<Lab[] | { data: Lab[] }>,
    labAccreditationsApi.index(),
  ]);
  labs.value = Array.isArray(labsData) ? labsData : labsData.data;
  allExpired.value = expiredData;
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('mouseup', onDocMouseUp);
  document.removeEventListener('keydown', keyboard.onKeyDown);
  document.removeEventListener('copy', clipboard.onCopy);
  document.removeEventListener('paste', clipboard.onPaste);
});

watch(selectedLabId, async (id) => {
  editing.value = null;
  selection.reset();
  clearMatchPrompt();
  rows.value = [];
  if (!id) return;
  loading.value = true;
  try {
    rows.value = await labAccreditationsApi.getByLab(id as number);
  } finally {
    loading.value = false;
  }
});

// ── Lab picker ────────────────────────────────────────────────────────────────
function selectLab(id: number | '') {
  selectedLabId.value = id;
  labPickerOpen.value = false;
}

// ── Render helpers ────────────────────────────────────────────────────────────
// Whether a cell is one of the "will also be updated" matches in the prompt row.
function isMatch(ri: number, ci: number): boolean {
  const m = matchPrompt.value;
  return !!m && m.ri === ri && m.cols.includes(COLS[ci]!);
}

// Draw the selection (blue) and, while dragging the handle, the fill target
// (green) as edge box-shadows — a crisp Excel-like border that doesn't reflow
// the layout the way real borders would under border-collapse.
function cellBorderStyle(ri: number, ci: number): string | undefined {
  const parts: string[] = [];
  const s = selection.selRect.value;
  // Suppress the rectangle outline while a non-contiguous set is active.
  if (!multiCells.value.size && inRect(s, ri, ci) && !(editing.value && isEditing(ri, ci))) {
    const c = '#2563eb';
    if (ri === s!.r1) parts.push(`inset 0 2px 0 ${c}`);
    if (ri === s!.r2) parts.push(`inset 0 -2px 0 ${c}`);
    if (ci === s!.c1) parts.push(`inset 2px 0 0 ${c}`);
    if (ci === s!.c2) parts.push(`inset -2px 0 0 ${c}`);
  }
  const f = fill.fillRect.value;
  if (inRect(f, ri, ci)) {
    const c = '#16a34a';
    if (ri === f!.r1) parts.push(`inset 0 2px 0 ${c}`);
    if (ri === f!.r2) parts.push(`inset 0 -2px 0 ${c}`);
    if (ci === f!.c1) parts.push(`inset 2px 0 0 ${c}`);
    if (ci === f!.c2) parts.push(`inset -2px 0 0 ${c}`);
  }
  return parts.length ? `box-shadow:${parts.join(',')}` : undefined;
}

// ── Bulk persistence ──────────────────────────────────────────────────────────
// Apply a map of rowIndex → {col: value} optimistically, then persist one upsert
// per touched row (each is keyed to a single analysis_type_id, so cross-row edits
// can't be batched). Rolls the whole batch back on any failure.
async function applyEdits(perRow: Map<number, Record<string, string | null>>) {
  const undo: Array<[LabAccreditationGridRow, string, string | null]> = [];
  const dirtyRows: number[] = [];
  for (const [ri, map] of perRow) {
    const row = rows.value[ri];
    if (!row) continue;
    let changed = false;
    for (const [col, val] of Object.entries(map)) {
      const prev = (row[col] as string | null) ?? null;
      if (prev === val) continue;
      undo.push([row, col, prev]);
      row[col] = val;
      changed = true;
    }
    if (changed) dirtyRows.push(ri);
  }
  if (dirtyRows.length === 0) return;
  try {
    await Promise.all(
      dirtyRows.map((ri) => {
        const row = rows.value[ri]!;
        return labAccreditationsApi.upsert(row.lab_id, row.analysis_type_id, perRow.get(ri)!);
      }),
    );
  } catch {
    for (const [row, col, prev] of undo) row[col] = prev;
    notify.error('Save failed — please try again');
    return;
  }
  // The lab-picker badges derive from the all-labs index loaded once on mount,
  // which the in-grid edits above don't touch — re-fetch so the counts don't go
  // stale until a hard refresh. Best-effort: a failure leaves the saved edits intact.
  try {
    allExpired.value = await labAccreditationsApi.index();
  } catch {
    /* counts refresh on next load; the edits themselves are saved */
  }
}

// ── Cell editing ──────────────────────────────────────────────────────────────
function startEdit(ri: number, ci: number, seed: string | null = null) {
  selection.setSelection({ ri, ci }, { ri, ci });
  editSeed.value = seed;
  editing.value = { ri, ci };
}

function cancelEdit() {
  editing.value = null;
  editSeed.value = null;
}

function autoFormatDate(e: Event) {
  const input = e.target as HTMLInputElement;
  input.value = formatDateInput(input.value);
}

async function commitEdit(
  e: Event,
  _row: LabAccreditationGridRow,
  col: string,
  ri: number,
  ci: number,
  move: 'down' | 'right' | null = null,
) {
  if (!isEditing(ri, ci)) return;
  const raw = (e.target as HTMLInputElement).value;
  editing.value = null;
  editSeed.value = null;
  clearMatchPrompt();
  selection.collapseMulti(); // a plain commit ends the multi-select intent
  const norm = normalizeDate(raw);
  if (norm.ok) {
    const row = rows.value[ri]!;
    const prev = (row[col] as string | null) ?? null;
    await applyEdits(new Map([[ri, { [col]: norm.value }]]));
    maybeOfferRowFill(ri, ci, prev, norm.value);
  }
  if (move === 'down') selection.moveSelection(1, 0);
  else if (move === 'right') selection.moveSelection(0, 1);
}

// After a renewal-style edit (old date → new date), surface the contextual prompt
// if other cells in the same row still carry the OLD date. Only real dates match —
// blanks are never propagated. Anchored just below the edited cell.
function maybeOfferRowFill(ri: number, ci: number, prev: string | null, next: string | null) {
  if (!prev || !next || prev === next) return;
  const row = rows.value[ri]!;
  const cols = COLS.filter((c, i) => i !== ci && ((row[c] as string | null) ?? null) === prev);
  if (cols.length === 0) return;
  nextTick(() => {
    const el = gridRef.value?.querySelector(`[data-cell="${ri}-${ci}"]`) as HTMLElement | null;
    const rect = el?.getBoundingClientRect();
    matchPrompt.value = {
      ri,
      cols,
      prev,
      next,
      top: rect ? rect.bottom + 4 : 0,
      left: rect ? rect.left : 0,
    };
  });
}

// Apply the prompt's new date to every matching cell in its row (one upsert).
function applyMatchPrompt() {
  const m = matchPrompt.value;
  if (!m) return;
  const map: Record<string, string | null> = {};
  for (const c of m.cols) map[c] = m.next;
  applyEdits(new Map([[m.ri, map]]));
  matchPrompt.value = null;
}

// ── Cell interaction (selection + fill wiring) ────────────────────────────────
// The engine composables own the state transitions; these thin handlers map raw
// DOM events onto the right intent and bridge to the view's editing state.
function onCellMouseDown(e: MouseEvent, ri: number, ci: number) {
  if (e.button !== 0 || editing.value) return;
  selection.beginInteraction();
  clearMatchPrompt();
  if (e.metaKey || e.ctrlKey) {
    selection.toggleMulti(ri, ci); // ⌘/Ctrl-click: toggle a scattered cell
    e.preventDefault();
    return;
  }
  if (e.shiftKey && selAnchor.value) {
    selection.extendTo(ri, ci); // shift-click: extend the range
    return;
  }
  selection.startAt(ri, ci); // plain click / drag start
}

function onCellMouseEnter(ri: number, ci: number) {
  if (selection.isSelecting.value) {
    // A drag off the anchor cancels the pending edit (the click became a drag).
    if (selection.dragTo(ri, ci)) editing.value = null;
  } else {
    fill.hoverCell(ri, ci);
  }
}

function onCellClick(ri: number, ci: number) {
  if (selection.consumeClick()) return; // swallow the click that ended a drag
  startEdit(ri, ci);
}

function onFillMouseDown(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
  if (!selection.selRect.value) return;
  // If a cell is mid-edit, commit its current text before filling from it.
  if (editing.value) {
    const input = gridRef.value?.querySelector('input.cell-input') as HTMLInputElement | null;
    const { ri, ci } = editing.value;
    editing.value = null;
    editSeed.value = null;
    if (input) {
      const norm = normalizeDate(input.value);
      if (norm.ok) applyEdits(new Map([[ri, { [COLS[ci]!]: norm.value }]]));
    }
  }
  fill.start();
}

function onDocMouseUp() {
  fill.finish(); // commits the drag-fill if one was in progress
  selection.endDrag();
}

// ── Cell input commit paths ───────────────────────────────────────────────────
// True when focus is inside a form field — used to defer to native behavior so we
// never hijack copy/paste/keys while the user is typing in the cell input.
function inFormField(): boolean {
  const ae = document.activeElement;
  return !!ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
}

function clearSelection() {
  const cells = selection.selectedCells();
  if (!cells.length) return;
  const perRow = new Map<number, Record<string, string | null>>();
  for (const [r, c] of cells) {
    const map = perRow.get(r) ?? {};
    map[COLS[c]!] = null;
    perRow.set(r, map);
  }
  applyEdits(perRow);
}

// Cmd/Ctrl+Enter from the cell input: write the typed value to EVERY selected
// cell (the rectangle or the non-contiguous set), including the edited one.
async function fillSelectionFromEdit(e: Event, ri: number, ci: number) {
  if (!isEditing(ri, ci)) return;
  const raw = (e.target as HTMLInputElement).value;
  editing.value = null;
  editSeed.value = null;
  clearMatchPrompt();
  const norm = normalizeDate(raw);
  if (!norm.ok) return;
  const keys = new Set(selection.selectedCells().map(([r, c]) => cellKey(r, c)));
  keys.add(cellKey(ri, ci));
  const perRow = new Map<number, Record<string, string | null>>();
  for (const k of keys) {
    const { ri: r, ci: c } = parseCellKey(k);
    const map = perRow.get(r) ?? {};
    map[COLS[c]!] = norm.value;
    perRow.set(r, map);
  }
  selection.collapseMulti();
  await applyEdits(perRow);
}

// Branch the input's Enter: Cmd/Ctrl+Enter fills the whole selection, plain Enter
// commits just this cell and steps down.
function onEnterCommit(e: Event, row: LabAccreditationGridRow, col: string, ri: number, ci: number) {
  const ke = e as KeyboardEvent;
  if (ke.metaKey || ke.ctrlKey) fillSelectionFromEdit(e, ri, ci);
  else commitEdit(e, row, col, ri, ci, 'down');
}

// Keep the active cell scrolled into view as it moves via the keyboard.
watch(selFocus, (f) => {
  if (!f || !gridRef.value) return;
  nextTick(() => {
    const el = gridRef.value?.querySelector(`[data-cell="${f.ri}-${f.ci}"]`) as HTMLElement | null;
    // Optional-chain the method too — jsdom (test env) has no scrollIntoView.
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  });
});
</script>

<template>
  <div class="accred">
    <h1>Expiration Dates</h1>
    <!-- ── Toolbar ──────────────────────────────────────────────────── -->
    <div class="toolbar">
      <div class="toolbar__lab">
        <label class="toolbar__label">Lab</label>
        <div ref="labPickerRef" class="lab-picker" :class="{ 'lab-picker--open': labPickerOpen }">
          <button type="button" class="lab-picker__trigger" @click="labPickerOpen = !labPickerOpen">
            <span class="lab-picker__selected">{{ selectedLab?.name ?? '— select a lab —' }}</span>
            <span v-if="selectedLabExpiredCount > 0" class="badge badge--expired badge--sm">
              {{ selectedLabExpiredCount }}
            </span>
            <svg class="lab-picker__chevron" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <ul v-show="labPickerOpen" class="lab-picker__menu">
            <li
              class="lab-picker__item"
              :class="{ 'lab-picker__item--selected': !selectedLabId }"
              @click="selectLab('')"
            >
              — select a lab —
            </li>
            <li
              v-for="lab in labsWithCounts"
              :key="lab.id"
              class="lab-picker__item"
              :class="{
                'lab-picker__item--selected': selectedLabId === lab.id,
                'lab-picker__item--expired': lab.expiredCount > 0,
              }"
              @click="selectLab(lab.id)"
            >
              <span class="lab-picker__name">{{ lab.name }}</span>
              <span v-if="lab.expiredCount > 0" class="badge badge--expired badge--sm">
                {{ lab.expiredCount }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div class="toolbar__right">
        <span
          v-if="expiredCount > 0"
          class="badge badge--expired"
          title="Expired cells in current lab"
        >
          {{ expiredCount }} expired
        </span>
      </div>
    </div>

    <!-- ── Empty / loading states ──────────────────────────────────── -->
    <div v-if="!selectedLabId" class="empty-state">
      Select a lab above to view and edit its accreditation dates.
    </div>
    <div v-else-if="loading" class="empty-state">Loading…</div>
    <div v-else-if="rows.length === 0" class="empty-state">
      No accreditation records found for this lab.
    </div>

    <!-- ── Spreadsheet grid ─────────────────────────────────────────── -->
    <div v-else ref="gridRef" class="grid-wrap">
      <table class="grid">
        <thead>
          <tr>
            <th class="col-at col-at--head">Analysis Type</th>
            <th v-for="col in COLS" :key="col" class="col-state">{{ col }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, ri) in rows"
            :key="row.id"
            :class="{ 'row--expired': COLS.some((c) => isExpired(row[c])) }"
          >
            <td class="col-at col-at--body">
              <div class="col-at__inner">
                <span class="col-at__name">{{ row.analysis_type }}</span>
                <span
                  v-if="rowExpiredCount(row) > 0"
                  class="badge badge--expired badge--sm"
                  :title="`${rowExpiredCount(row)} expired date(s) in this row`"
                >
                  {{ rowExpiredCount(row) }}
                </span>
              </div>
            </td>
            <td
              v-for="(col, ci) in COLS"
              :key="col"
              class="cell"
              :data-cell="`${ri}-${ci}`"
              :class="{
                'cell--expired': isExpired(row[col]),
                'cell--editing': isEditing(ri, ci),
                'cell--filled': !!row[col],
                'cell--selected': isSelected(ri, ci) && !isEditing(ri, ci),
                'cell--match': isMatch(ri, ci),
              }"
              :style="cellBorderStyle(ri, ci)"
              @mousedown="onCellMouseDown($event, ri, ci)"
              @mouseenter="onCellMouseEnter(ri, ci)"
              @click="onCellClick(ri, ci)"
              @dblclick="startEdit(ri, ci)"
            >
              <input
                v-if="isEditing(ri, ci)"
                v-focus
                type="text"
                class="cell-input"
                :value="editSeed !== null ? editSeed : cellValue(row, col)"
                placeholder="YYYY-MM-DD"
                maxlength="10"
                @mousedown.stop
                @input="autoFormatDate"
                @blur="commitEdit($event, row, col, ri, ci)"
                @keydown.enter.prevent="onEnterCommit($event, row, col, ri, ci)"
                @keydown.tab.prevent="commitEdit($event, row, col, ri, ci, 'right')"
                @keydown.escape.prevent="cancelEdit()"
              />
              <span v-else class="cell-text">{{ cellValue(row, col) }}</span>
              <span
                v-if="isFillHandle(ri, ci)"
                class="fill-handle"
                title="Drag to fill"
                @mousedown="onFillMouseDown($event)"
              ></span>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td :colspan="COLS.length + 1" class="empty-row">
              No records for this lab.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Keyboard hint ────────────────────────────────────────────── -->
    <p v-if="selectedLabId && rows.length" class="grid-hint">
      Tip: drag the corner handle to fill · ⌘/Ctrl-click to pick scattered cells,
      then type a date and press ⌘/Ctrl+Enter to fill them all.
    </p>

    <!-- ── "Also update matching cells" popover (Option 3) ──────────── -->
    <div
      v-if="matchPrompt"
      class="match-pop"
      :style="{ top: `${matchPrompt.top}px`, left: `${matchPrompt.left}px` }"
      @mousedown.stop
    >
      <div class="match-pop__text">
        {{ matchPrompt.cols.length }}
        other {{ matchPrompt.cols.length > 1 ? 'columns' : 'column' }} in this row
        {{ matchPrompt.cols.length > 1 ? 'are' : 'is' }} on
        <strong>{{ matchPrompt.prev }}</strong> — update to
        <strong>{{ matchPrompt.next }}</strong>?
      </div>
      <div class="match-pop__actions">
        <!-- Act on mousedown, not click: firing before the press can blur a still-
             focused cell input avoids that input's @blur → commitEdit →
             clearMatchPrompt closing the popover before this handler runs.
             .prevent keeps the input from blurring at all. -->
        <button type="button" class="match-pop__btn" @mousedown.prevent.stop="clearMatchPrompt">
          Dismiss
        </button>
        <button
          type="button"
          class="match-pop__btn match-pop__btn--primary"
          @mousedown.prevent.stop="applyMatchPrompt"
        >
          Update {{ matchPrompt.cols.length }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Shell ───────────────────────────────────────────────────────────── */
.accred {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Toolbar ─────────────────────────────────────────────────────────── */
.toolbar {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.toolbar__lab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toolbar__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

.toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

/* ── Lab picker ──────────────────────────────────────────────────────── */
.lab-picker {
  position: relative;
  min-width: 260px;
}

.lab-picker__trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.35rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  font: inherit;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  text-align: left;
}

.lab-picker__trigger:hover {
  border-color: #9ca3af;
}

.lab-picker--open .lab-picker__trigger {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  outline: none;
}

.lab-picker__selected {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lab-picker__chevron {
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.15s;
}

.lab-picker--open .lab-picker__chevron {
  transform: rotate(180deg);
}

.lab-picker__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  min-width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 50;
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
}

.lab-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.lab-picker__item:hover {
  background: #f3f4f6;
}

.lab-picker__item--selected {
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 500;
}

.lab-picker__item--expired {
  color: #b91c1c;
}

.lab-picker__item--expired:hover {
  background: #fef2f2;
}

.lab-picker__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Badges ──────────────────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
}

.badge--expired {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.badge--sm {
  font-size: 0.7rem;
  padding: 0.1rem 0.4rem;
}

/* ── Empty / loading ─────────────────────────────────────────────────── */
.empty-state {
  padding: 2.5rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.9rem;
  border: 1px dashed #e5e7eb;
  border-radius: 0.5rem;
}

/* ── Grid wrapper ────────────────────────────────────────────────────── */
.grid-wrap {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  max-height: calc(100vh - 180px);
}

/* ── Grid table ──────────────────────────────────────────────────────── */
.grid {
  border-collapse: collapse;
  font-size: 0.8125rem;
  width: max-content;
  min-width: 100%;
  /* Suppress native text selection while drag-selecting cells. */
  user-select: none;
}

/* ── Sticky header row ───────────────────────────────────────────────── */
.grid thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f3f4f6;
  border-bottom: 2px solid #d1d5db;
  border-right: 1px solid #e5e7eb;
  padding: 0.35rem 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  text-align: center;
  user-select: none;
}

/* ── Sticky analysis-type column ─────────────────────────────────────── */
.col-at {
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 180px;
  background: #f9fafb;
  border-right: 2px solid #d1d5db;
}

.col-at--head {
  z-index: 4;
  text-align: left;
}

.col-at--body {
  padding: 0 0.5rem;
  font-weight: 500;
  color: #374151;
  height: 32px;
  border-bottom: 1px solid #e5e7eb;
}

.col-at__inner {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 100%;
}

.col-at__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── State columns ───────────────────────────────────────────────────── */
.col-state {
  min-width: 120px;
}

/* ── Data cells ──────────────────────────────────────────────────────── */
.cell {
  height: 32px;
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #f3f4f6;
  cursor: cell;
  position: relative;
  min-width: 120px;
}

.cell:hover:not(.cell--editing) {
  background: #eff6ff;
}

.cell--expired {
  background: #fff1f2;
}

.cell--expired:hover:not(.cell--editing) {
  background: #ffe4e6;
}

.cell--editing {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
  background: #fff !important;
  z-index: 1;
}

/* ── Range selection ─────────────────────────────────────────────────── */
.cell--selected {
  background: #e8f0fe;
}

.cell--selected:hover:not(.cell--editing) {
  background: #dbe7fd;
}

.cell--selected.cell--expired {
  background: #fbdfe2;
}

/* ── Matching cells the prompt will update (amber ring) ──────────────── */
.cell--match::after {
  content: '';
  position: absolute;
  inset: 0;
  box-shadow: inset 0 0 0 2px #f59e0b;
  pointer-events: none;
  z-index: 2;
}

/* ── "Also update matching cells" popover ────────────────────────────── */
.match-pop {
  position: fixed;
  z-index: 60;
  max-width: 260px;
  padding: 0.6rem 0.7rem;
  background: #1e293b;
  color: #f8fafc;
  border-radius: 0.375rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  font-size: 0.8rem;
}

.match-pop__text {
  margin-bottom: 0.55rem;
  line-height: 1.4;
}

.match-pop__text strong {
  font-weight: 600;
  white-space: nowrap;
}

.match-pop__actions {
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
}

.match-pop__btn {
  font: inherit;
  font-size: 0.78rem;
  padding: 0.28rem 0.7rem;
  border: 1px solid transparent;
  border-radius: 0.3rem;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
}

.match-pop__btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.match-pop__btn--primary {
  background: #2563eb;
  color: #fff;
}

.match-pop__btn--primary:hover {
  background: #1d4ed8;
}

/* ── Keyboard hint ───────────────────────────────────────────────────── */
.grid-hint {
  margin: 0;
  font-size: 0.75rem;
  color: #9ca3af;
}

/* ── Fill handle (drag to copy down / across) ────────────────────────── */
.fill-handle {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 8px;
  height: 8px;
  background: #2563eb;
  border: 1px solid #fff;
  border-radius: 1px;
  cursor: crosshair;
  z-index: 5;
}

/* ── Cell text ───────────────────────────────────────────────────────── */
.cell-text {
  display: block;
  padding: 0 0.4rem;
  line-height: 32px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
}

.cell--expired .cell-text {
  color: #dc2626;
  font-weight: 500;
}

/* ── Cell input ──────────────────────────────────────────────────────── */
.cell-input {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0 0.3rem;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 0.8125rem;
  color: #111827;
  box-sizing: border-box;
  /* Re-enable native selection inside the editable input. */
  user-select: text;
}

/* ── Row with any expired cell ───────────────────────────────────────── */
.row--expired td {
  background: #fffafa;
}

.row--expired .col-at--body {
  background: #fef2f2;
  border-left: 3px solid #dc2626;
  color: #dc2626;
  font-weight: 600;
}

.row--expired .cell--expired {
  background: #fff1f2;
}

/* ── Empty row ───────────────────────────────────────────────────────── */
.empty-row {
  padding: 1.5rem;
  text-align: center;
  color: #9ca3af;
}
</style>
